import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { useMemo, useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import { useChat } from "@/features/chat"
import { advisorsList, type Advisor } from "@/features/advisors"

import {
  FOCUS_ADVISORS,
  QUIZ_STEPS,
  RESULT_BADGES,
  type QuizAnswerKey,
} from "../../config/quiz-steps"
import { QuizQuestion } from "./quiz-question"
import { QuizLoading } from "./quiz-loading"
import { QuizResult, type MatchedAdvisor } from "./quiz-result"

const LOADING_STEP = QUIZ_STEPS.length // 6
const RESULT_STEP = QUIZ_STEPS.length + 1 // 7

interface AdvisorsQuizModalProps {
  /** Back out of the quiz (returns to the profile). */
  onClose: () => void
  /** A chat was started — let the caller close the surrounding profile modal. */
  onStarted: () => void
}

/** Find-my-advisor quiz: 6 questions → loading → matched advisors. Ported from AdvisorsQuizModal. */
export function AdvisorsQuizModal({ onClose, onStarted }: AdvisorsQuizModalProps) {
  const navigate = useNavigate()
  const { setSelectedAdvisor } = useChat()
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Partial<Record<QuizAnswerKey, string>>>({})

  const matched = useMemo<MatchedAdvisor[]>(() => {
    const indices = answers.focusArea ? FOCUS_ADVISORS[answers.focusArea] : undefined
    if (!indices) return []
    return indices
      .map((i) => advisorsList[i])
      .filter(Boolean)
      .map((advisor, i) => ({ badge: RESULT_BADGES[i] ?? "Top Match", advisor }))
  }, [answers])

  const selectAnswer = (key: QuizAnswerKey, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }))
    setStep((prev) => prev + 1)
  }

  const goBack = () => {
    if (step <= 0) onClose()
    else setStep((prev) => prev - 1)
  }

  const handleStartChat = (advisor: Advisor) => {
    setSelectedAdvisor(advisor)
    navigate({ to: WebappRoutes.READINGS })
    onStarted()
  }

  const screen = () => {
    if (step < QUIZ_STEPS.length) {
      const quizStep = QUIZ_STEPS[step]
      return (
        <QuizQuestion
          step={quizStep}
          index={step}
          onBack={goBack}
          onSelect={(value) => selectAnswer(quizStep.key, value)}
        />
      )
    }
    if (step === LOADING_STEP) return <QuizLoading onComplete={() => setStep(RESULT_STEP)} />
    return <QuizResult advisors={matched} onClose={onClose} onStartChat={handleStartChat} />
  }

  return (
    <div className="fixed inset-0 z-[1060] mx-auto max-h-[100dvh] w-full max-w-app overflow-y-auto bg-dark-base [scrollbar-width:none]">
      {screen()}
    </div>
  )
}
